/**
 * Sale status → `canStartPurchase` derivation.
 *
 * Frontend policy (5.a):
 *   * active generation MUST be V2 (only V2 is purchasable)
 *   * V2 status MUST have `state === "ok"`  and  `paused === false`
 *
 * "degraded" / "not_ready" / "configuration_error" / "not_configured"
 * disable the purchase UI. "paused" also disables it, with a specific
 * reason so the UI can surface it distinctly.
 */
import type { SaleStatus } from "./sale-types";

export type PurchaseGate =
  | { canStartPurchase: true; reason: null }
  | {
      canStartPurchase: false;
      reason:
        | "generation_not_v2"
        | "sale_paused"
        | "backend_not_ready"
        | "backend_degraded"
        | "sale_not_configured"
        | "sale_configuration_error";
    };

export function derivePurchaseGate(status: SaleStatus | null): PurchaseGate {
  if (status == null) {
    return { canStartPurchase: false, reason: "backend_not_ready" };
  }
  if (status.state === "not_configured") {
    return { canStartPurchase: false, reason: "sale_not_configured" };
  }
  if (status.state === "configuration_error") {
    return { canStartPurchase: false, reason: "sale_configuration_error" };
  }
  if (status.state === "not_ready") {
    return { canStartPurchase: false, reason: "backend_not_ready" };
  }
  if (status.state === "degraded") {
    return { canStartPurchase: false, reason: "backend_degraded" };
  }
  // state === "ok"
  if (status.generation !== "v2") {
    return { canStartPurchase: false, reason: "generation_not_v2" };
  }
  if (status.paused) {
    return { canStartPurchase: false, reason: "sale_paused" };
  }
  return { canStartPurchase: true, reason: null };
}
